/**
 * liveAbilityCast — sandbox (x/z) bridge onto abilityDispatcher (engine x/y).
 * Presentation only spawns VFX from returned liveProjectiles.
 */

import { dispatchAbility } from './abilityDispatcher.js';
import { getAbilityByChampSlot } from '../domain/abilities/abilityCatalog.js';
import { queueCastWindup } from '../domain/abilities/abilityCastTiming.js';
import { dispatchOnLivePlane, LIVE_WORLD_SCALE } from './liveAbilityPlane.js';
import './championKitBoot.js';

/**
 * @param {{ championKey?: string, name?: string } | null | undefined} entity
 * @param {string} [fallback]
 */
export function resolveChampionKey(entity, fallback = 'ashe') {
  const raw = entity?.championKey || entity?.name || fallback;
  return String(raw).toLowerCase();
}

/**
 * @param {{ vx?: number, vy?: number, range?: number, damage?: number, isUlt?: boolean, id?: string }} p
 * @param {string|null} [vfxId]
 */
export function engineToLiveProjectile(p, vfxId = null) {
  if (p.isHawkshot) {
    return {
      id: p.id || 'hawk',
      type: 'hawkshot_e',
      speed: p.speed || 2400,
      targetX: p.targetX,
      targetZ: p.targetY,
      damage: 0,
      vfxId
    };
  }
  const speed = Math.hypot(p.vx || 0, p.vy || 0) || 1200;
  const engineRadius = Number.isFinite(p.radius) ? p.radius : 40;
  return {
    id: p.id || 'proj',
    angle: Math.atan2(p.vx || 0, p.vy || 0),
    speed: Math.min(2400, speed),
    maxDistance: Math.min(24, (p.range || 800) / 80),
    damage: p.damage || 0,
    type: p.isUlt ? 'ultimate_r' : 'skillshot_w',
    hitRadius: Math.max(0.15, engineRadius * LIVE_WORLD_SCALE),
    vfxId
  };
}

/**
 * @param {{
 *   player: object,
 *   abilityKey: string,
 *   cursor?: { x?: number, z?: number, y?: number } | null,
 *   gameTime?: number,
 *   entities?: object[],
 *   selectedTarget?: object | null,
 *   champKey?: string | null
 * }} opts
 */
function queueVeigarWWindup(player, abilitySpec, opts, vfxId) {
  queueCastWindup(player, {
    abilityKey: 'W',
    champKey: 'veigar',
    cursor: opts.cursor,
    entities: opts.entities,
    selectedTarget: opts.selectedTarget,
    gameTime: opts.gameTime || 0,
    castTime: abilitySpec?.castTime,
    effectDelay: abilitySpec?.effectDelay,
    duration: abilitySpec?.duration
  });
  return {
    type: 'windup',
    cooldown: abilitySpec?.cooldown ?? 0,
    vfxId,
    projectiles: [],
    liveProjectiles: []
  };
}

export function castLiveAbility(opts) {
  const player = opts.player;
  if (!player) return null;
  const abilityKey = opts.abilityKey;
  const key = (opts.champKey || resolveChampionKey(player)).toLowerCase();
  const abilitySpec = getAbilityByChampSlot(key, abilityKey);
  const vfxId = abilitySpec?.vfxId || null;
  if (!opts.skipWindup && key === 'veigar' && abilityKey === 'W') {
    return queueVeigarWWindup(player, abilitySpec, opts, vfxId);
  }
  const bucket = [];
  const result = dispatchOnLivePlane(player, opts, (worldPos) => dispatchAbility(
    key,
    player,
    abilityKey,
    worldPos,
    bucket,
    opts.gameTime || 0,
    opts.entities || [],
    opts.selectedTarget || null
  ));
  if (!result) return null;
  return {
    type: result.type,
    cooldown: result.cooldown,
    vfxId,
    projectiles: result.projectiles || [],
    liveProjectiles: (result.projectiles || []).map((p) => engineToLiveProjectile(p, vfxId))
  };
}
